
import React, { memo, useMemo, useCallback, useRef, useEffect, useState, useLayoutEffect } from 'react';
import { VariableSizeList as List } from 'react-window';
import {
  Box,
  Card,
  Accordion,
  AccordionSummary,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MeetingCard from './MeetingCard';
import { pinedKey, idKey, titleKey, isSummarizedKey, dateKey, summaryKey } from './MeetingsKeysList';
import { getSentence } from './translations';

// Constants for different item types in the virtualized list
const ITEM_TYPES = {
  START_SPACER: 'start_spacer',
  END_SPACER: 'end_spacer',
  SECTION_SPACER: 'section_spacer',
  PINNED_HEADER: 'pinned_header',
  REGULAR_HEADER: 'regular_header',
  MEETING_CARD: 'meeting_card'
};

// Fixed heights for consistent calculations
const ITEM_HEIGHTS = {
  [ITEM_TYPES.START_SPACER]: 20,
  [ITEM_TYPES.END_SPACER]: 158, // Same height as meeting card
  [ITEM_TYPES.SECTION_SPACER]: 24, // Small spacing between sections
  [ITEM_TYPES.PINNED_HEADER]: 56,
  [ITEM_TYPES.REGULAR_HEADER]: 56,
  [ITEM_TYPES.MEETING_CARD]: 158 // 150px card + 8px padding
};

// Utility functions moved outside component to prevent recreation
const splitMeetings = (meetings) => ({
  pinnedMeetings: meetings.filter(meeting => meeting[pinedKey]),
  regularMeetings: meetings.filter(meeting => !meeting[pinedKey])
});

const createVirtualItems = (pinnedMeetings, regularMeetings, pinnedAccordionExpanded, regularAccordionExpanded) => {
  const items = [];
  
  // Initial spacer
  items.push({ type: ITEM_TYPES.START_SPACER, id: 'start-spacer' });
  
  // Pinned section
  if (pinnedMeetings.length > 0) {
    items.push({ 
      type: ITEM_TYPES.PINNED_HEADER, 
      id: 'pinned-header',
      sectionType: 'pinned'
    });
    
    if (pinnedAccordionExpanded) {
      pinnedMeetings.forEach(meeting => {
        items.push({ 
          type: ITEM_TYPES.MEETING_CARD, 
          id: meeting[idKey],
          meeting,
          sectionType: 'pinned'
        });
      });
    }
  }
  
  // Add spacing between sections if both exist and pinned is expanded
  if (pinnedMeetings.length > 0 && regularMeetings.length > 0 && pinnedAccordionExpanded) {
    items.push({ type: ITEM_TYPES.SECTION_SPACER, id: 'section-spacer' });
  }
  
  // Regular section
  if (regularMeetings.length > 0) {
    items.push({ 
      type: ITEM_TYPES.REGULAR_HEADER, 
      id: 'regular-header',
      sectionType: 'regular'
    });
    
    if (regularAccordionExpanded) {
      regularMeetings.forEach(meeting => {
        items.push({ 
          type: ITEM_TYPES.MEETING_CARD, 
          id: meeting[idKey],
          meeting,
          sectionType: 'regular'
        });
      });
    }
  }
  
  // End spacer for smooth scrolling (same height as meeting card)
  items.push({ type: ITEM_TYPES.END_SPACER, id: 'end-spacer' });
  
  return items;
};

// Custom hook for sticky header logic
const useStickyHeader = (virtualItems, pinnedMeetings, regularMeetings, pinnedAccordionExpanded, regularAccordionExpanded, onPinnedAccordionChange, onRegularAccordionChange) => {
  const [stickyHeader, setStickyHeader] = useState(null);

  const calculateStickyHeader = useCallback((scrollOffset) => {
    let currentOffset = 0;
    let activeSection = null;
    
    // Find which section we're currently viewing
    for (const item of virtualItems) {
      const itemHeight = ITEM_HEIGHTS[item.type];
      
      if (scrollOffset >= currentOffset && scrollOffset < currentOffset + itemHeight) {
        if (item.type === ITEM_TYPES.MEETING_CARD || 
            item.type === ITEM_TYPES.PINNED_HEADER || 
            item.type === ITEM_TYPES.REGULAR_HEADER) {
          activeSection = item.sectionType;
        }
        break;
      }
      
      currentOffset += itemHeight;
    }
    
    // Set sticky header based on active section
    if (activeSection === 'pinned' && pinnedMeetings.length > 0) {
      setStickyHeader({
        title: getSentence(2),
        count: pinnedMeetings.length,
        expanded: pinnedAccordionExpanded,
        onChange: onPinnedAccordionChange,
        sectionType: 'pinned'
      });
    } else if (activeSection === 'regular' && regularMeetings.length > 0) {
      setStickyHeader({
        title: getSentence(1),
        count: regularMeetings.length,
        expanded: regularAccordionExpanded,
        onChange: onRegularAccordionChange,
        sectionType: 'regular'
      });
    } else {
      setStickyHeader(null);
    }
  }, [virtualItems, pinnedMeetings.length, regularMeetings.length, 
      pinnedAccordionExpanded, regularAccordionExpanded, 
      onPinnedAccordionChange, onRegularAccordionChange]);

  return { stickyHeader, calculateStickyHeader };
};



// Header component for reuse between sticky and regular headers
const SectionHeader = memo(({ 
  title, 
  count, 
  expanded, 
  onChange, 
  style = {} 
}) => (
  <div style={style}>
    <Accordion
      expanded={expanded}
      onChange={onChange}
      elevation={0}
      disableGutters
      style={{ backgroundColor: 'transparent', boxShadow: 'none' }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon style={{ color: 'var(--textWhite)' }} />}
        className="divider"
        style={{ 
          minHeight: 'auto', 
          padding: '0px 16px', 
          margin: '0px 0px',
          borderRadius: '0px 0px 0px 0px'
        }}
      >
        <Typography 
          style={{ 
            color: 'var(--textWhite)', 
            fontWeight: 'bold', 
            margin: '0px 0px', 
            padding: '0px 0px' 
          }}
        >
          {title} ({count})
        </Typography>
      </AccordionSummary>
    </Accordion>
  </div>
));

SectionHeader.displayName = 'SectionHeader';

const MeetingsList = memo(({
  meetings,
  selectedMeetingId,
  pinnedAccordionExpanded,
  regularAccordionExpanded,
  onMeetingSelect,
  onMeetingDelete,
  onMeetingTogglePin,
  onMeetingShare,
  onPinnedAccordionChange,
  onRegularAccordionChange
}) => {
  const listRef = useRef(null);
  const containerRef = useRef(null);
  const [containerHeight, setContainerHeight] = useState(600);
  const scrollPositionRef = useRef(0);
  const lastStructureRef = useRef('');
  const isUpdatingRef = useRef(false);

  // Split meetings into pinned and regular (memoized for performance)
  const { pinnedMeetings, regularMeetings } = useMemo(() => splitMeetings(meetings), [meetings]);

  // Create virtualized items structure
  const virtualItems = useMemo(() => 
    createVirtualItems(pinnedMeetings, regularMeetings, pinnedAccordionExpanded, regularAccordionExpanded),
    [pinnedMeetings, regularMeetings, pinnedAccordionExpanded, regularAccordionExpanded]
  );

  // Use custom hook for sticky header logic
  const { stickyHeader, calculateStickyHeader } = useStickyHeader(
    virtualItems, 
    pinnedMeetings, 
    regularMeetings, 
    pinnedAccordionExpanded, 
    regularAccordionExpanded, 
    onPinnedAccordionChange, 
    onRegularAccordionChange
  );

  // Track structural changes to trigger cache reset
  const structureSignature = useMemo(() => 
    `${pinnedMeetings.length}-${regularMeetings.length}-${pinnedAccordionExpanded}-${regularAccordionExpanded}`,
    [pinnedMeetings.length, regularMeetings.length, pinnedAccordionExpanded, regularAccordionExpanded]
  );

  // Update container height on resize
  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setContainerHeight(rect.height);
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);



  // Handle scroll events
  const handleScroll = useCallback(({ scrollOffset }) => {
    if (!isUpdatingRef.current) {
      scrollPositionRef.current = scrollOffset;
      calculateStickyHeader(scrollOffset);
    }
  }, [calculateStickyHeader]);

  // Get item height
  const getItemHeight = useCallback((index) => {
    const item = virtualItems[index];
    return ITEM_HEIGHTS[item.type] || 50;
  }, [virtualItems]);

  // Seamlessly handle structural changes without flickering
  useLayoutEffect(() => {
    const hasStructuralChange = lastStructureRef.current !== structureSignature;
    
    if (hasStructuralChange && listRef.current) {
      isUpdatingRef.current = true;
      const currentScroll = scrollPositionRef.current;
      
      // Reset cache to prevent overlapping
      listRef.current.resetAfterIndex(0, true);
      
      // Calculate safe scroll position
      const totalHeight = virtualItems.reduce((total, _, index) => 
        total + (ITEM_HEIGHTS[virtualItems[index].type] || 50), 0);
      const maxScrollOffset = Math.max(0, totalHeight - containerHeight);
      const safeScrollOffset = Math.min(Math.max(0, currentScroll), maxScrollOffset);
      
      // Apply scroll position immediately without animation
      if (safeScrollOffset !== currentScroll || hasStructuralChange) {
        listRef.current.scrollTo(safeScrollOffset);
        scrollPositionRef.current = safeScrollOffset;
        calculateStickyHeader(safeScrollOffset);
      }
      
      // Mark update as complete
      lastStructureRef.current = structureSignature;
      isUpdatingRef.current = false;
    }
  }, [structureSignature, virtualItems, containerHeight, calculateStickyHeader]);

  // Wrapper functions that preserve scroll position
  const createScrollPreservingHandler = useCallback((originalHandler) => {
    return (...args) => {
      // Save current scroll position before the action
      if (listRef.current && !isUpdatingRef.current) {
        scrollPositionRef.current = listRef.current.state?.scrollOffset || 0;
      }
      originalHandler(...args);
    };
  }, []);

  const handleMeetingTogglePin = useMemo(() => 
    createScrollPreservingHandler(onMeetingTogglePin), 
    [createScrollPreservingHandler, onMeetingTogglePin]
  );

  const handleMeetingDelete = useMemo(() => 
    createScrollPreservingHandler(onMeetingDelete), 
    [createScrollPreservingHandler, onMeetingDelete]
  );

  // Also wrap onMeetingSelect and onMeetingShare for consistency
  const handleMeetingSelect = useMemo(() => 
    createScrollPreservingHandler(onMeetingSelect), 
    [createScrollPreservingHandler, onMeetingSelect]
  );

  const handleMeetingShare = useMemo(() => 
    createScrollPreservingHandler(onMeetingShare), 
    [createScrollPreservingHandler, onMeetingShare]
  );

  const handlePinnedAccordionChange = useMemo(() => 
    createScrollPreservingHandler(onPinnedAccordionChange), 
    [createScrollPreservingHandler, onPinnedAccordionChange]
  );

  const handleRegularAccordionChange = useMemo(() => 
    createScrollPreservingHandler(onRegularAccordionChange), 
    [createScrollPreservingHandler, onRegularAccordionChange]
  );

  // Render individual items
  const renderItem = useCallback(({ index, style }) => {
    const item = virtualItems[index];
    
    switch (item.type) {
      case ITEM_TYPES.START_SPACER:
      case ITEM_TYPES.END_SPACER:
      case ITEM_TYPES.SECTION_SPACER:
        return <div key={item.id} style={style}><Card /></div>;
        
      case ITEM_TYPES.PINNED_HEADER:
        return (
          <SectionHeader
            key={item.id}
            title={getSentence(2)}
            count={pinnedMeetings.length}
            expanded={pinnedAccordionExpanded}
            onChange={handlePinnedAccordionChange}
            style={style}
          />
        );
        
      case ITEM_TYPES.REGULAR_HEADER:
        return (
          <SectionHeader
            key={item.id}
            title={getSentence(1)}
            count={regularMeetings.length}
            expanded={regularAccordionExpanded}
            onChange={handleRegularAccordionChange}
            style={style}
          />
        );
        
      case ITEM_TYPES.MEETING_CARD:
        return (
          <div key={item.id} style={{ ...style, paddingTop: '8px' }}>
            <MeetingCard
              meeting={item.meeting}
              isSelected={selectedMeetingId === item.meeting[idKey]}
              onSelect={handleMeetingSelect}
              onDelete={handleMeetingDelete}
              onTogglePin={handleMeetingTogglePin}
              onShare={handleMeetingShare}
            />
          </div>
        );
        
      default:
        return <div key={item.id || `default-${index}`} style={style} />;
    }
  }, [
    virtualItems,
    pinnedMeetings.length,
    regularMeetings.length,
    pinnedAccordionExpanded,
    regularAccordionExpanded,
    selectedMeetingId,
    handleMeetingSelect,
    handleMeetingShare,
    handleMeetingDelete,
    handleMeetingTogglePin,
    handlePinnedAccordionChange,
    handleRegularAccordionChange
  ]);

  // Don't render if no meetings (but still show search bar if there are meetings to search)
  if (meetings.length === 0) {
    return null;
  }

  return (
    <Box ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Sticky Header */}
        {stickyHeader && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              backgroundColor: 'var(--gradientStart)',
              borderBottom: '1px solid #ddd'
            }}
          >
            <SectionHeader
              title={stickyHeader.title}
              count={stickyHeader.count}
              expanded={stickyHeader.expanded}
              onChange={stickyHeader.onChange}
            />
          </Box>
        )}
        
        {/* Virtualized List */}
        <List
          ref={listRef}
          height={containerHeight}
          itemCount={virtualItems.length}
          itemSize={getItemHeight}
          width="100%"
          onScroll={handleScroll}
          style={{ 
            paddingTop: stickyHeader ? '56px' : '0px' // Space for sticky header
          }}
        >
          {renderItem}
        </List>
    </Box>
  );
});

MeetingsList.displayName = 'MeetingsList';

// Custom comparison function for MeetingsList memo
const arePropsEqual = (prevProps, nextProps) => {
  // Compare meetings array - check if it's the same reference or has the same content
  if (prevProps.meetings !== nextProps.meetings) {
    if (prevProps.meetings.length !== nextProps.meetings.length) {
      return false;
    }
    
    // Deep comparison of meetings - check if any meeting data actually changed
    for (let i = 0; i < prevProps.meetings.length; i++) {
      const prevMeeting = prevProps.meetings[i];
      const nextMeeting = nextProps.meetings[i];
      
      // Compare essential meeting properties
      if (
        prevMeeting[idKey] !== nextMeeting[idKey] ||
        prevMeeting[titleKey] !== nextMeeting[titleKey] ||
        prevMeeting[dateKey] !== nextMeeting[dateKey] ||
        prevMeeting[summaryKey] !== nextMeeting[summaryKey] ||
        prevMeeting[pinedKey] !== nextMeeting[pinedKey] ||
        prevMeeting[isSummarizedKey] !== nextMeeting[isSummarizedKey]
      ) {
        return false;
      }
    }
  }
  
  // Compare other essential props
  const essentialPropsChanged = (
    prevProps.selectedMeetingId !== nextProps.selectedMeetingId ||
    prevProps.pinnedAccordionExpanded !== nextProps.pinnedAccordionExpanded ||
    prevProps.regularAccordionExpanded !== nextProps.regularAccordionExpanded
  );
  
  if (essentialPropsChanged) {
    return false;
  }
  
  // Check if callback functions changed (they shouldn't if properly memoized)
  const callbacksChanged = (
    prevProps.onMeetingSelect !== nextProps.onMeetingSelect ||
    prevProps.onMeetingDelete !== nextProps.onMeetingDelete ||
    prevProps.onMeetingTogglePin !== nextProps.onMeetingTogglePin ||
    prevProps.onMeetingShare !== nextProps.onMeetingShare ||
    prevProps.onPinnedAccordionChange !== nextProps.onPinnedAccordionChange ||
    prevProps.onRegularAccordionChange !== nextProps.onRegularAccordionChange
  );
  
  if (callbacksChanged) {
    return false;
  }
  
  return true; // No significant change, don't re-render
};

export default memo(MeetingsList, arePropsEqual); 